import { LEARNER_AGE_LIMITS } from '@iqmaxx/shared';

import { apiGet, apiPost } from '../../../adapters/api-client/api';

export interface StudentListItem {
  studentId: string;
  firstName: string;
  lastName: string;
  grade: number;
  age: number;
}

export interface CreateStudentInput {
  firstName: string;
  lastName: string;
  /** ISO date, YYYY-MM-DD. The API derives age from this. */
  birthDate: string;
  grade: number;
  /**
   * PRT-08-FR-01 AC-03 — constant across retries of one create, so the server
   * answers a replay with the learner it already made instead of a twin.
   */
  idempotencyKey?: string;
}

/**
 * A fresh key for one learner-create intent.
 *
 * `crypto.randomUUID` exists only in secure contexts on recent browsers; the
 * fallback is still 122 random bits from the platform RNG, never Math.random.
 */
export function newIdempotencyKey(): string {
  const c: Crypto | undefined = (globalThis as any).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  c!.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20,
  )}-${hex.slice(20)}`;
}

/**
 * Backend rules, so the form fails before the request. The age range is the
 * server's own value (DEC-S2-S02-03), not a mirror of it; the rest is mirrored.
 */
export const STUDENT_LIMITS = {
  ...LEARNER_AGE_LIMITS,
  minGrade: 1,
  maxGrade: 12,
  maxPerParent: 5,
  nameMaxLength: 50,
} as const;

/** Age exactly as students.service.ts computes it. */
export function ageFromBirthDate(birthDate: string, today = new Date()): number {
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

/**
 * Fetch all student profiles owned by the authenticated parent.
 * GET /api/v1/students
 */
export function getStudents(): Promise<StudentListItem[]> {
  return apiGet<StudentListItem[]>('/api/v1/students');
}

/**
 * Create a student profile under the authenticated parent.
 * POST /api/v1/students
 */
export function createStudent(data: CreateStudentInput): Promise<StudentListItem> {
  return apiPost<StudentListItem>('/api/v1/students', data);
}
